import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useSearchParams } from 'react-router-dom';

const LeadsPage = () => {
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get('campaign');
  
  const [leads, setLeads] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [agents, setAgents] = useState({});
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('user');
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [allColumns, setAllColumns] = useState([]);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const leadsPerPage = 100;

  // Options de statut personnalisées avec couleurs
  const statusOptions = [
    { value: 'nouveau', label: 'Nouveau', bg: '#dbeafe', color: '#1e40af' },
    { value: 'bv', label: 'BV', bg: '#fef3c7', color: '#92400e' },
    { value: 'occupe', label: 'Occupé', bg: '#fee2e2', color: '#991b1b' },
    { value: 'vente', label: 'Vente', bg: '#dcfce7', color: '#166534' },
    { value: 'rdv', label: 'RDV', bg: '#e9d5ff', color: '#6b21a8' },
    { value: 'rappel', label: 'Rappel', bg: '#cffafe', color: '#0891b2' },
    { value: 'non_interesse', label: 'Non intéressé', bg: '#f3f4f6', color: '#4b5563' },
    { value: 'pas_repondu', label: 'Pas répondu', bg: '#f3f4f6', color: '#4b5563' },
    { value: 'a_rappeler', label: 'À rappeler', bg: '#fff3cd', color: '#856404' },
    { value: 'qualifie', label: 'Qualifié', bg: '#d1fae5', color: '#065f46' },
    { value: 'converti', label: 'Converti', bg: '#e9d5ff', color: '#6b21a8' },
    { value: 'perdu', label: 'Perdu', bg: '#fee2e2', color: '#991b1b' }
  ];

  // Créer un mapping pour un accès rapide
  const statusMap = {};
  statusOptions.forEach(opt => {
    statusMap[opt.value] = opt;
  });

  useEffect(() => {
    console.log('Campaign ID from URL:', campaignId); // Debug
    if (campaignId && campaignId !== 'ID_CAMPAGNE_1' && campaignId !== 'ID_CAMPAGNE_2') {
      loadCampaignInfo();
    }
    loadUserAndLeads();
    loadAgents();
  }, [campaignId]);

  const loadCampaignInfo = async () => {
    const { data, error } = await supabase
      .from('campaigns')
      .select('name')
      .eq('id', campaignId)
      .single();
    
    if (!error && data) {
      setCampaign(data);
    }
  };

  const loadAgents = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('id, email, full_name');
    
    const agentMap = {};
    (data || []).forEach(agent => {
      agentMap[agent.id] = agent;
    });
    setAgents(agentMap);
  };

  const loadUserAndLeads = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();
        
        if (profile) {
          setUserRole(profile.role);
        }
      }
      
      await fetchLeads(user);
      
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const fetchLeads = async (currentUser) => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false });
      
      // Filtrer par campagne si un ID valide est présent
      if (campaignId && campaignId !== 'ID_CAMPAGNE_1' && campaignId !== 'ID_CAMPAGNE_2') {
        console.log('Filtering by campaign:', campaignId);
        query = query.eq('campaign_id', campaignId);
      }
      
      if (userRole === 'agent' && currentUser) {
        query = query.eq('assigned_to', currentUser.id);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      console.log('Leads found:', data?.length);
      setLeads(data || []);
      
      // Détecter toutes les colonnes disponibles
      if (data && data.length > 0) {
        const columns = new Set();
        data.forEach(lead => {
          Object.keys(lead).forEach(key => {
            if (!['id', 'created_at', 'updated_at', 'user_id', 'company_id', 'created_by', 
                   'list_id', 'campaign_id', 'import_file_id', 'assigned_to', 
                   'call_attempts', 'last_call_at', 'call_duration', 'last_call_status',
                   'activity_log', 'photos', 'notes_list', 'materials', 'documents',
                   'original_row_index', 'call_status', 'call_result', 'next_follow_up',
                   'assignment_status', 'pipeline_id', 'rating', 'value'].includes(key)) {
              
              const hasData = data.some(l => l[key] !== null && l[key] !== undefined && l[key] !== '');
              if (hasData) {
                columns.add(key);
              }
            }
          });
        });
        
        const priorityColumns = [
          'first_name', 'last_name', 'email', 'phone', 'company', 
          'address', 'city', 'postal_code', 'country', 'position', 
          'source', 'notes', 'budget', 'quoted_value', 'job_value',
          'service', 'timeline', 'installation_date', 'visit_date'
        ];
        
        const sortedColumns = [...columns].sort((a, b) => {
          const aIndex = priorityColumns.indexOf(a);
          const bIndex = priorityColumns.indexOf(b);
          if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          return a.localeCompare(b);
        });
        
        setAllColumns(sortedColumns);
        const defaultColumns = sortedColumns.filter(col => 
          ['first_name', 'last_name', 'email', 'phone', 'company'].includes(col)
        );
        setSelectedColumns(defaultColumns.length > 0 ? defaultColumns : sortedColumns.slice(0, 5));
      }
      
    } catch (error) {
      console.error('Erreur chargement leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      const { error } = await supabase
        .from('leads')
        .update({ status: newStatus })
        .eq('id', leadId);
      
      if (error) throw error;
      
      setLeads(leads.map(lead => 
        lead.id === leadId ? { ...lead, status: newStatus } : lead
      ));
    } catch (error) {
      console.error('Erreur mise à jour statut:', error);
    }
  };

  const toggleColumn = (column) => {
    if (selectedColumns.includes(column)) {
      setSelectedColumns(selectedColumns.filter(c => c !== column));
    } else {
      setSelectedColumns([...selectedColumns, column]);
    }
  };

  const formatColumnName = (col) => {
    const names = {
      first_name: 'Prénom',
      last_name: 'Nom',
      email: 'Email',
      phone: 'Téléphone',
      company: 'Entreprise',
      address: 'Adresse',
      city: 'Ville',
      postal_code: 'Code postal',
      country: 'Pays',
      position: 'Poste',
      website: 'Site web',
      notes: 'Notes',
      source: 'Source',
      budget: 'Budget',
      quoted_value: 'Devis',
      job_value: 'Valeur'
    };
    return names[col] || col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatValue = (value, column) => {
    if (value === null || value === undefined || value === '') return '-';
    
    if (column === 'phone' && value) {
      const phoneValue = value.toString().replace(/\s/g, '');
      return (
        <a 
          href={`tel:${phoneValue}`}
          style={{
            color: '#2563eb',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.25rem'
          }}
          title="Cliquer pour appeler"
        >
          📞 {value}
        </a>
      );
    }
    
    if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
    if (value instanceof Date || (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/))) {
      return new Date(value).toLocaleDateString('fr-FR');
    }
    if (typeof value === 'number') {
      if (Number.isInteger(value)) return value.toString();
      return value.toFixed(2);
    }
    if (typeof value === 'object') return JSON.stringify(value).slice(0, 30);
    return value.toString();
  };

  // Pagination
  const indexOfLastLead = currentPage * leadsPerPage;
  const indexOfFirstLead = indexOfLastLead - leadsPerPage;
  const currentLeads = leads.slice(indexOfFirstLead, indexOfLastLead);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));

  useEffect(() => {
    setTotalPages(Math.ceil(leads.length / leadsPerPage));
    setCurrentPage(1);
  }, [leads]);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f3f4f6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        Chargement des leads...
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f3f4f6',
      padding: '2rem',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{
        maxWidth: '1600px',
        margin: '0 auto',
        width: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* En-tête avec compteur */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div>
            <h1 style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              color: '#111827',
              margin: 0
            }}>
              {campaign ? `Leads - ${campaign.name}` : (userRole === 'agent' ? 'Mes leads' : 'Gestion des leads')}
            </h1>
            <p style={{
              margin: '0.5rem 0 0 0',
              color: '#6b7280',
              fontSize: '0.875rem'
            }}>
              {leads.length} lead{leads.length > 1 ? 's' : ''} au total
              {leads.length > leadsPerPage && ` • Page ${currentPage}/${totalPages}`}
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: '1rem' }}>
            {allColumns.length > 0 && (
              <button
                onClick={() => setShowColumnSelector(!showColumnSelector)}
                style={{
                  backgroundColor: showColumnSelector ? '#6b7280' : '#2563eb',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <span>⚙️</span>
                {showColumnSelector ? 'Fermer' : 'Colonnes'}
              </button>
            )}
          </div>
        </div>

        {/* Sélecteur de colonnes */}
        {showColumnSelector && allColumns.length > 0 && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.5rem',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>
              Colonnes disponibles ({selectedColumns.length}/{allColumns.length})
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: '0.5rem'
            }}>
              {allColumns.map(col => (
                <label
                  key={col}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem',
                    backgroundColor: selectedColumns.includes(col) ? '#dbeafe' : '#f9fafb',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedColumns.includes(col)}
                    onChange={() => toggleColumn(col)}
                    style={{ cursor: 'pointer' }}
                  />
                  {formatColumnName(col)}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Tableau des leads */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          overflow: 'hidden',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            overflowX: 'auto',
            flex: 1,
            position: 'relative'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              minWidth: '800px'
            }}>
              <thead style={{
                backgroundColor: '#f9fafb',
                position: 'sticky',
                top: 0,
                zIndex: 10,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <tr>
                  {selectedColumns.map(col => (
                    <th key={col} style={{
                      ...tableHeaderStyle,
                      borderRight: 'none'
                    }}>
                      {formatColumnName(col)}
                    </th>
                  ))}
                  <th style={{
                    ...tableHeaderStyle,
                    minWidth: '140px',
                    borderRight: 'none'
                  }}>
                    Statut
                  </th>
                  {userRole !== 'agent' && (
                    <th style={{
                      ...tableHeaderStyle,
                      minWidth: '150px'
                    }}>
                      Assigné à
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {currentLeads.map(lead => {
                  const status = statusMap[lead.status] || statusMap['nouveau'];
                  const agent = agents[lead.assigned_to];
                  
                  return (
                    <tr
                      key={lead.id}
                      style={{
                        borderBottom: 'none'
                      }}
                    >
                      {selectedColumns.map(col => (
                        <td key={col} style={{
                          ...tableCellStyle,
                          borderRight: 'none'
                        }}>
                          {formatValue(lead[col], col)}
                        </td>
                      ))}
                      
                      <td style={{
                        ...tableCellStyle,
                        borderRight: 'none',
                        backgroundColor: status.bg,
                        padding: '0.5rem'
                      }}>
                        <select
                          value={lead.status || 'nouveau'}
                          onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: 'none',
                            borderRadius: '0.375rem',
                            backgroundColor: status.bg,
                            color: status.color,
                            fontWeight: '500',
                            fontSize: '0.875rem',
                            cursor: 'pointer'
                          }}
                        >
                          {statusOptions.map(opt => (
                            <option 
                              key={opt.value} 
                              value={opt.value}
                              style={{
                                backgroundColor: opt.bg,
                                color: opt.color
                              }}
                            >
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      
                      {userRole !== 'agent' && (
                        <td style={tableCellStyle}>
                          {agent ? (
                            <span style={{
                              display: 'inline-block',
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#dbeafe',
                              color: '#1e40af',
                              borderRadius: '0.25rem',
                              fontSize: '0.875rem',
                              fontWeight: '500'
                            }}>
                              {agent.full_name || agent.email}
                            </span>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>-</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
                {currentLeads.length === 0 && (
                  <tr>
                    <td colSpan={selectedColumns.length + (userRole !== 'agent' ? 2 : 1)} style={{
                      padding: '4rem',
                      textAlign: 'center',
                      color: '#6b7280'
                    }}>
                      <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📭</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: '500' }}>
                        Aucun lead trouvé pour cette campagne
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            marginTop: '1.5rem',
            marginBottom: '1rem',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <button
              onClick={prevPage}
              disabled={currentPage === 1}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: currentPage === 1 ? '#e5e7eb' : '#2563eb',
                color: currentPage === 1 ? '#6b7280' : 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontWeight: '500',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              ← Précédent
            </button>
            
            <div style={{
              display: 'flex',
              gap: '0.25rem'
            }}>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => paginate(pageNum)}
                    style={{
                      padding: '0.5rem 0.75rem',
                      backgroundColor: currentPage === pageNum ? '#2563eb' : 'white',
                      color: currentPage === pageNum ? 'white' : '#111827',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.375rem',
                      fontWeight: currentPage === pageNum ? '600' : '400',
                      cursor: 'pointer'
                    }}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={nextPage}
              disabled={currentPage === totalPages}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: currentPage === totalPages ? '#e5e7eb' : '#2563eb',
                color: currentPage === totalPages ? '#6b7280' : 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontWeight: '500',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
              }}
            >
              Suivant →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const tableHeaderStyle = {
  padding: '0.75rem 1rem',
  textAlign: 'left',
  fontSize: '0.75rem',
  fontWeight: '600',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: '#4b5563',
  borderBottom: '2px solid #e5e7eb',
  whiteSpace: 'nowrap',
  backgroundColor: '#f9fafb'
};

const tableCellStyle = {
  padding: '0.75rem 1rem',
  fontSize: '0.875rem',
  color: '#111827',
  borderBottom: '1px solid #e5e7eb',
  verticalAlign: 'middle'
};

export default LeadsPage;
